<template>
  <div>
    <b-row>
      <b-col cols="8">
        <TrackerLabel
          label-name="Campaign Tracking"
        />
      </b-col>
      <b-col
        cols="4"
        class="text-right"
      >
        <b-button
          data-test="tracker-save-btn"
          variant="primary"
          :class="{ 'btn-loading': isSaveBtnLoading }"
          @click="saveChanges()"
          :disabled="!canSaveChanges"
        >
          Save
        </b-button>
      </b-col>
    </b-row>

    <p class="mt-3">
      Add tags below to track campaign performance on 3rd party platforms.
    </p>
    <b-form-group
      id="impression-tracker-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="impression-tracker-input"
      >
        <h4
          class="form-label"
        >
          Impression tag (image)
        </h4>
      </template>
      <b-form-textarea
        id="impression-tracker-input"
        :class="{ 'is-invalid': !isValidState('impressionTracker'), 'is-valid': this.trackers['impressionTracker'].id }"
        :value="impressionTrackerValue"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateNewTrackers('impressionTracker', $event)"
        placeholder="Enter a URL"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="impression-tracker-input"
      />
      <p
        v-show="!isValidState('impressionTracker')"
        class="mb-0 text-danger"
      >
        <small>{{ trackers['impressionTracker'].error }}</small>
      </p>
    </b-form-group>
    <b-form-group
      id="click-tracker-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="click-tracker-input"
      >
        <h4
          class="form-label"
        >
          Click tag
        </h4>
      </template>
      <b-form-textarea
        id="click-tracker-input"
        :class="{ 'is-invalid': !isValidState('clickTracker'), 'is-valid': this.trackers['impressionTracker'].id }"
        :value="clickTrackerValue"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateNewTrackers('clickTracker', $event)"
        placeholder="Enter a URL"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="click-tracker-input"
      />
      <p
        v-show="!isValidState('clickTracker')"
        class="mb-0 text-danger"
      >
        <small>{{ trackers['clickTracker'].error }}</small>
      </p>
    </b-form-group>
    <b-form-group
      id="post-click-tracker-input"
      class="mb-5"
    >
      <template
        v-slot:label
        label-for="post-click-tracker-input"
      >
        <h4
          class="form-label"
        >
          Post-Click
        </h4>
      </template>
      <b-form-textarea
        id="post-click-tracker-input"
        :class="{ 'is-invalid': !isValidState('postClickTracker'), 'is-valid': this.trackers['impressionTracker'].id }"
        :value="postClickTrackerValue"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateNewTrackers('postClickTracker', $event)"
        placeholder="Enter a Post-Click Tracker"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="post-click-tracker-input"
      />
      <p
        v-show="!isValidState('postClickTracker')"
        class="mb-0 text-danger"
      >
        <small>{{ trackers['postClickTracker'].error }}</small>
      </p>
    </b-form-group>
    <h5 class="font-weight-bold">
      Additional 3rd party tags
    </h5>
    <b-form-group
      v-for="(tag, index) in trackers['thirdPartyTrackers'].values"
      id="third-party-tracker-inputs"
      class="mb-5"
      :key="index"
    >
      <div class="row">
        <div class="col form-label mt-2">
          {{ tag.type === 'THIRD_PARTY_IMAGE' ? 'Image Tracker' : 'Script Tracker' }}
        </div>
        <div class="col text-right">
          <b-button
            data-test="tracker-save-btn"
            variant="danger"
            :class="{ 'btn-loading': isSaveBtnLoading }"
            @click="deleteThirdPartyTracker(index, tag)"
          >
            <i class="fal fa-trash-can" />
          </b-button>
        </div>
      </div>
      <p
        class="form-label"
      />
      <b-form-textarea
        id="third-party-tracker-input"
        v-model="tag.newValue"
        :class="{ 'is-invalid': tag.hasOwnProperty('error'), 'is-valid': tag.id && !tag.hasOwnProperty('error') }"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @input="updateThirdPartyTrackers($event, tag)"
        placeholder="Enter a third party Tracker"
        rows="5"
        max-rows="5"
        no-resize
        :disabled="!canUpdateTrackers"
        data-test="third-party-tracker-input"
      />
      <p
        class="mb-0 text-danger"
      >
        <small>{{ tag.error }}</small>
      </p>
    </b-form-group>
    <div class="row">
      <div class="col">
        <b-form-select
          v-model="selectedTrackerType"
          :options="trackerTypeOptions"
        />
      </div>
      <div class="col">
        <b-button
          variant="primary"
          @click="addThirdPartyTag()"
          :disabled="!selectedTrackerType || GET_IS_CAMPAIGN_READONLY"
        >
          Add tag
        </b-button>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-await-in-loop */
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as trackerTypes from '@/modules/trackers/store/types';
import * as contentTypes from '@/modules/content/store/types';
import TrackerLabel from './TrackerLabel.vue';

function initialData() {
  return {
    isRequestingData: false,
    isSaveBtnLoading: false,
    isDirty: false,
    trackers: {
      impressionTracker: {
        type: 'IMPRESSION',
        id: null,
        value: null,
      },
      clickTracker: {
        type: 'CLICK',
        id: null,
        value: null,
      },
      postClickTracker: {
        type: 'POST_CLICK',
        id: null,
        value: null,
      },
      thirdPartyTrackers: {
        type: 'THIRD_PARTY',
        values: [],
      },
    },
    trackerTypeOptions: [
      { text: 'Tag Type', value: null },
      { text: 'Script Tracker', value: 'THIRD_PARTY_SCRIPT' },
      { text: 'Image Tracker', value: 'THIRD_PARTY_IMAGE' },
    ],
    selectedTrackerType: null,
  };
}
export default {
  name: 'TrackersProperties',
  components: { TrackerLabel },
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    selectedCreativeArticleCombo() {
      return `${this.selectedCreativeId}-${this.selectedArticleUuid}`;
    },
    impressionTrackerValue() {
      if (this.trackers.impressionTracker.hasOwnProperty('newValue')) return this.trackers.impressionTracker.newValue;
      return this.trackers.impressionTracker.value;
    },
    clickTrackerValue() {
      if (this.trackers.clickTracker.hasOwnProperty('newValue')) return this.trackers.clickTracker.newValue;
      return this.trackers.clickTracker.value;
    },
    postClickTrackerValue() {
      if (this.trackers.postClickTracker.hasOwnProperty('newValue')) return this.trackers.postClickTracker.newValue;
      return this.trackers.postClickTracker.value;
    },
    canUpdateTrackers() {
      return !this.GET_IS_CAMPAIGN_READONLY
        && !this.isRequestingData;
    },
    canSaveChanges() {
      return !this.GET_IS_CAMPAIGN_READONLY
        && this.canUpdateTrackers
        && this.isDirty
        && !this.isSaveBtnLoading;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    ...mapActions('trackers', [
      trackerTypes.FETCH_SELF_SERVICE_CONTENT_TRACKERS,
      trackerTypes.MANUALLY_ENTER_TRACKERS,
      trackerTypes.MANUALLY_UPDATE_TRACKER,
      trackerTypes.DELETE_TRACKERS,
    ]),
    updateNewTrackers(trackerType, value) {
      this.$set(this.$data.trackers[trackerType], 'newValue', value);
      this.isDirty = true;
      if (this.trackers[trackerType].hasOwnProperty('error')) delete this.trackers[trackerType].error;
    },
    updateThirdPartyTrackers(value, existingTracker) {
      this.isDirty = true;
      if (existingTracker.hasOwnProperty('error')) delete existingTracker.error;
    },
    deleteThirdPartyTracker(index, tracker) {
      if (index !== -1) this.trackers.thirdPartyTrackers.values.splice(index, 1);

      this.trackers.thirdPartyTrackers.values.forEach((thirdPartyTracker, trackerIndex) => {
        thirdPartyTracker.index = trackerIndex;
      });

      if (tracker.id) this.DELETE_TRACKERS([tracker.id]);
    },
    hasChanged(trackerType) {
      const tracker = this.trackers[trackerType];
      if (tracker.hasOwnProperty('newValue')) {
        if (!tracker.newValue && !tracker.value) return false;
        return tracker.newValue !== tracker.value;
      }
      return false;
    },
    hasThirdPartyTrackerChanged(tracker) {
      if (tracker.hasOwnProperty('newValue')) {
        if (!tracker.newValue && !tracker.value) return false;
        return tracker.newValue !== tracker.value;
      }
      return false;
    },
    async createTracker(trackerType, tracker) {
      const payload = {
        campaignId: this.campaign.id,
        trackerInfo: {
          trackers: [tracker].map((trackerToSave) => trackerToSave.newValue),
          scope: 'CONTENT_ITEM',
          type: tracker.type,
          creativeId: this.selectedCreativeId,
          referenceId: this.selectedArticleUuid,
        },
      };
      return await this.MANUALLY_ENTER_TRACKERS(payload)
        .then((response) => {
          this.setUpdatedValues({
            trackerType,
            trackerId: response.trackers[0].trackerId,
            value: response.trackers[0].value,
            index: tracker.index,
          });
        })
        .catch(() => {
          if (tracker.type === 'THIRD_PARTY_SCRIPT' || tracker.type === 'THIRD_PARTY_IMAGE') {
            const index = this.trackers.thirdPartyTrackers.values.findIndex((thirdPartyTracker) => thirdPartyTracker.index === tracker.index);
            this.$set(this.$data.trackers.thirdPartyTrackers.values[index], 'error', 'Tag not supported');
          } else {
            this.$set(this.$data.trackers[trackerType], 'error', 'Tag not supported');
          }
        });
    },
    async updateTracker(trackerType, tracker) {
      const payload = {
        trackerId: tracker.id,
        trackerValue: tracker.newValue,
      };
      return await this.MANUALLY_UPDATE_TRACKER(payload)
        .then((response) => {
          this.setUpdatedValues({
            trackerType,
            trackerId: response.trackerId,
            value: response.value,
            index: tracker.index,
          });
        })
        .catch(() => {
          if (tracker.type === 'THIRD_PARTY_SCRIPT' || tracker.type === 'THIRD_PARTY_IMAGE') {
            const index = this.trackers.thirdPartyTrackers.values.findIndex((thirdPartyTracker) => thirdPartyTracker.id === tracker.id);
            this.$set(this.$data.trackers.thirdPartyTrackers.values[index], 'error', 'Tag not supported');
          } else {
            this.$set(this.$data.trackers[trackerType], 'error', 'Tag not supported');
          }
        });
    },
    async deleteTracker(trackerType, trackerId) {
      return await this.DELETE_TRACKERS([trackerId])
        .then(() => {
          this.setUpdatedValues({
            trackerType,
            trackerId: null,
            value: null,
          });
        });
    },
    async saveChanges() {
      if (!this.canSaveChanges) return;
      this.isSaveBtnLoading = true;
      this.isRequestingData = true;
      try {
        for (const trackerType in this.trackers) {
          const tracker = this.trackers[trackerType];

          if (tracker.hasOwnProperty('values')) {
            tracker.values.forEach((trackerValue, index) => {
              if (!trackerValue.newValue) tracker.values.splice(index, 1);
            });

            tracker.values
              .filter((thirdPartyTracker) => this.hasThirdPartyTrackerChanged(thirdPartyTracker) && !thirdPartyTracker.hasOwnProperty('error'))
              .forEach((thirdPartyTracker) => this.addOrModifyThirdPartyTrackers(thirdPartyTracker));
          } else {
            await this.addOrModifyTracker(trackerType, tracker);
          }
        }
      } finally {
        this.isSaveBtnLoading = false;
        this.isRequestingData = false;
        this.isDirty = false;
      }
    },
    async addOrModifyTracker(trackerType, tracker) {
      if (this.hasChanged(trackerType)) {
        if (!tracker.id) await this.createTracker(trackerType, tracker);
        else if (!tracker.newValue) await this.deleteTracker(trackerType, tracker.id);
        else await this.updateTracker(trackerType, tracker);
      }
    },
    async addOrModifyThirdPartyTrackers(tracker) {
      if (!tracker.id) {
        await this.createTracker(tracker.type, tracker);
      } else {
        await this.updateTracker(tracker.type, tracker);
      }
    },
    setUpdatedValues(payload) {
      if (payload.trackerType !== 'THIRD_PARTY_SCRIPT' && payload.trackerType !== 'THIRD_PARTY_IMAGE') {
        this.trackers[payload.trackerType].id = payload.trackerId;
        this.trackers[payload.trackerType].value = payload.value;
        delete this.trackers[payload.trackerType].newValue;
        if (this.trackers[payload.trackerType].hasOwnProperty('error')) delete this.trackers[payload.trackerType].error;
      } else {
        this.$set(this.$data.trackers.thirdPartyTrackers.values[payload.index], 'id', payload.trackerId);
        this.$set(this.$data.trackers.thirdPartyTrackers.values[payload.index], 'value', payload.value);
        if (this.$data.trackers.thirdPartyTrackers.values[payload.index].hasOwnProperty('error')) delete this.$data.trackers.thirdPartyTrackers.values[payload.index].error;
      }
    },
    isValidState(trackerType) {
      return !this.trackers[trackerType].hasOwnProperty('error');
    },
    async fetchTrackers() {
      this.isDirty = false;
      this.isRequestingData = true;
      await this.FETCH_SELF_SERVICE_CONTENT_TRACKERS({ creativeId: this.selectedCreativeId })
        .then((trackers) => {
          if (trackers.length === 0) return;
          const trackersForArticle = trackers.find((trackerSet) => trackerSet.contentItemUuid === this.selectedArticleUuid);
          if (!trackersForArticle) return;
          const trackersTypes = Object.keys(this.trackers);

          trackersTypes.forEach((type) => {
            if (type === 'thirdPartyTrackers') {
              const thirdPartyScriptTrackers = trackersForArticle.thirdPartyScriptTrackers ? trackersForArticle.thirdPartyScriptTrackers.map((tracker) => ({
                ...tracker, type: 'THIRD_PARTY_SCRIPT', newValue: tracker.value,
              })) : [];

              const thirdPartyImageTrackers = trackersForArticle.thirdPartyImageTrackers ? trackersForArticle.thirdPartyImageTrackers.map((tracker) => ({
                ...tracker, type: 'THIRD_PARTY_IMAGE', newValue: tracker.value,
              })) : [];

              const thirdPartyTrackers = thirdPartyScriptTrackers.concat(thirdPartyImageTrackers)
                .filter((tracker) => tracker);
              thirdPartyTrackers.sort((a, b) => a.id - b.id);

              this.trackers.thirdPartyTrackers.values = thirdPartyTrackers
                .map((tracker, index) => ({ ...tracker, index }));
            } else {
              if (!trackersForArticle[type]) return;
              this.trackers[type].id = trackersForArticle[type].id || null;
              this.trackers[type].value = trackersForArticle[type].value || null;
            }
          });
        });
      this.isRequestingData = false;
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
    addThirdPartyTag() {
      if (this.selectedTrackerType) {
        this.trackers.thirdPartyTrackers.values.push({
          type: this.selectedTrackerType,
          value: '',
          index: this.trackers.thirdPartyTrackers.values.length,
        });

        this.isDirty = true;
      }
    },
  },
  mounted() {
    this.fetchTrackers();
  },
  watch: {
    selectedCreativeArticleCombo(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetData();
        this.fetchTrackers();
      }
    },
  },
};
</script>
